import Vue from 'vue'
import axios from 'axios'
import { Auth as RepoAuth } from '@finx/web-repository/src/services/Oidc'
import Interceptor from '@finx/web-repository/src/apis/Interceptor'
import _ from 'lodash'
import { User as Usr } from '@/types/_'
import { objectKeysToCamelCase } from '@/utils/common'

declare global {
  interface Window {
    API_FINX_AUTH: string;
    ENV: string;
  }
}

const defaultUser: Usr = {
  accessToken: '',
  expiresAt: '',
  idToken: '',
  profile: {
    authTime: '',
    email: '',
    emailVerified: [],
    name: '',
    preferredUsername: '',
    pageAuthorized: [],
    roles: [],
    sid: '',
    sub: '',
    defaultPage: {
      keyAuthorized: '',
      view: '',
    },
  },
  refreshToken: '',
  scope: '',
  sessionState: '',
  tokenType: '',
}

// Tenta ler de múltiplas chaves de storage para suportar tanto o formato antigo quanto o novo
// IMPORTANTE: Isso também suporta personificação de usuários via Token Exchange
let item: any = null
const legacyKey = `oidc.user:${window.API_FINX_AUTH}:portal-client-2`
item = localStorage.getItem(legacyKey)

if (!item) {
  const keycloakUrl = (window as any).KEYCLOAK_AUTHORITY_URL || window.API_FINX_AUTH
  const realm = (window as any).KEYCLOAK_REALM || 'finx'
  const clientId = (window as any).KEYCLOAK_CLIENT_ID || 'finx-front'
  const newKey = `oidc.user:${keycloakUrl}/realms/${realm}:${clientId}`
  item = localStorage.getItem(newKey)
}

let user: any = JSON.parse(item) ? objectKeysToCamelCase(JSON.parse(item)) : defaultUser
user.profile.emailVerified = user.profile.emailVerified || false

// PROTEÇÃO: Interceptor de localStorage para personificação
const originalSetItem = localStorage.setItem
localStorage.setItem = function setItemInterceptor(key: string, value: string) {
  // PROTEÇÃO: Se há impersonação ativa, BLOQUEAR criação de novos dados OIDC
  // (code verifiers, states, etc) MAS permitir salvar as chaves do usuário
  const isImpersonating = sessionStorage.getItem('IMPERSONATION_ACTIVE')
  const isImpersonationInProgress = sessionStorage.getItem('IMPERSONATION_IN_PROGRESS')

  // Só ativa proteção durante processo de personificação
  if ((isImpersonating === 'true' || isImpersonationInProgress === 'true') && key && key.startsWith('oidc.')) {
    const keycloakUrl = (window as any).KEYCLOAK_AUTHORITY_URL || window.API_FINX_AUTH
    const realm = (window as any).KEYCLOAK_REALM || 'finx'
    const clientId = (window as any).KEYCLOAK_CLIENT_ID || 'finx-front'
    const storageKey1 = `oidc.user:${keycloakUrl}/realms/${realm}:${clientId}`
    const storageKey2 = `oidc.user:${window.API_FINX_AUTH}:portal-client-2`

    // Segurança extra: se não há token ativo em localStorage, estamos em re-login —
    // não bloquear, pois impedir o fluxo OIDC aqui deixa o usuário preso na tela de login
    const hasActiveToken = !!(localStorage.getItem(storageKey1) || localStorage.getItem(storageKey2))
    if (!hasActiveToken) {
      return originalSetItem.call(this, key, value)
    }

    // Permite apenas as chaves do usuário personificado
    if (key !== storageKey1 && key !== storageKey2) {
      // Em vez de bloquear silenciosamente, log em dev mode
      if (window.ENV !== 'production') {
        console.warn('[Impersonation] Bloqueado localStorage.setItem para:', key)
      }
      return undefined
    }
  }
  return originalSetItem.call(this, key, value)
}

if (!user.profile.roles || Array.isArray(user.profile.roles)) {
  user.profile.roles = []
} else if (typeof user.profile.roles === 'string') {
  user.profile.roles = [user.profile.roles]
} else {
  user.profile.roles = Object.values(user.profile.roles)
}

if (!user.profile.pageAuthorized || Array.isArray(user.profile.pageAuthorized)) {
  user.profile.pageAuthorized = []
} else if (typeof user.profile.pageAuthorized === 'string') {
  user.profile.pageAuthorized = [user.profile.pageAuthorized]
} else {
  user.profile.pageAuthorized = Object.values(user.profile.pageAuthorized)
}

// Enable route '/ajuda' for doctors and secretaries...
if (
  user.profile.pageAuthorized
  && Array.isArray(user.profile.pageAuthorized)
  && (user.profile.pageAuthorized.includes('agendamento-medico') || user.profile.pageAuthorized.includes('agendamento-medico-ambulatorio'))
) {
  user.profile.pageAuthorized.push('ajuda')
}

const User = {
  install(options: any) {
    const defaultOptions = options

    // MODIFICADO: $user agora é reativo para suportar personificação
    Object.defineProperty(Vue.prototype, '$user', {
      get() {
        return user
      },
      set(newValue) {
        user = newValue
      },
      configurable: true,
    })

    // se já houver token no localStorage, configure o header Authorization do axios
    const accessToken = user.access_token || user.accessToken || (user.profile && user.profile.access_token)
    if (accessToken) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`
    }

    // Monkeypatch Auth.getAccessToken to prefer the token we stored for oidc-client
    try {
      const originalGetAccessToken = (RepoAuth as any).getAccessToken && (RepoAuth as any).getAccessToken.bind(RepoAuth)
      const oidcAuthority = `${(window as any).KEYCLOAK_AUTHORITY_URL}/realms/${(window as any).KEYCLOAK_REALM}`
      const oidcClientId = (window as any).KEYCLOAK_CLIENT_ID
      const OIDC_STORE_KEY = `oidc.user:${oidcAuthority}:${oidcClientId}`

      ;(RepoAuth as any).getAccessToken = async function(returnUrl?: string) {
        // eslint-disable-next-line no-console
        console.debug('[User plugin] patched Auth.getAccessToken called, checking localStorage key', OIDC_STORE_KEY)
        try {
          const item = localStorage.getItem(OIDC_STORE_KEY)
          if (item) {
            try {
              const parsed = JSON.parse(item)
              if (parsed && parsed.access_token) {
                // eslint-disable-next-line no-console
                console.debug('[User plugin] returning access_token from localStorage oidc key (masked)', parsed.access_token ? `${String(parsed.access_token).substr(0,6)}...` : null)
                return parsed.access_token
              }
            } catch (parseErr) {
              // eslint-disable-next-line no-console
              console.warn('[User plugin] failed to parse oidc-client storage value', parseErr)
            }
          } else {
            // eslint-disable-next-line no-console
            console.debug('[User plugin] no oidc-client key found in localStorage')
          }
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn('[User plugin] failed to read oidc-client key, falling back to original getAccessToken', e)
        }

        if (originalGetAccessToken) {
          // eslint-disable-next-line no-console
          console.debug('[User plugin] falling back to original Auth.getAccessToken')
          return await originalGetAccessToken(returnUrl)
        }
        return null
      }
      // eslint-disable-next-line no-console
      console.debug('[User plugin] patched Auth.getAccessToken installed, OIDC_STORE_KEY =', OIDC_STORE_KEY)
    } catch (e) {
      // ignore if repo Auth not available
    }

    // Também patch para Interceptor.defineHeader para garantir que, antes de qualquer request
    // feita por classes que usam `new Interceptor()`, tentemos o token do localStorage.
    try {
      const originalDefineHeader = (Interceptor as any).prototype.defineHeader
      ;(Interceptor as any).prototype.defineHeader = async function() {
        // eslint-disable-next-line no-console
        console.debug('[User plugin] Interceptor.defineHeader patched: checking localStorage for token before calling Auth')
        try {
          const oidcAuthority = `${(window as any).KEYCLOAK_AUTHORITY_URL}/realms/${(window as any).KEYCLOAK_REALM}`
          const oidcClientId = (window as any).KEYCLOAK_CLIENT_ID
          const OIDC_STORE_KEY = `oidc.user:${oidcAuthority}:${oidcClientId}`
          const item = localStorage.getItem(OIDC_STORE_KEY)
          if (item) {
            try {
              const parsed = JSON.parse(item)
              if (parsed && parsed.access_token) {
                (this as any).token = parsed.access_token
                // set global axios header
                axios.defaults.headers.common.Authorization = `Bearer ${parsed.access_token}`
                // eslint-disable-next-line no-console
                console.debug('[User plugin] Interceptor used access_token from localStorage (masked)', parsed.access_token ? `${String(parsed.access_token).substr(0,6)}...` : null)
                return
              }
            } catch (parseErr) {
              // eslint-disable-next-line no-console
              console.warn('[User plugin] failed to parse oidc-client storage value in Interceptor.defineHeader', parseErr)
            }
          }
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn('[User plugin] error checking localStorage in Interceptor.defineHeader', e)
        }

        if (originalDefineHeader) {
          return await originalDefineHeader.apply(this, arguments)
        }
      }
      // eslint-disable-next-line no-console
      console.debug('[User plugin] patched Interceptor.defineHeader')
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[User plugin] could not patch Interceptor.defineHeader', e)
    }
  },
}

export default User